/**
 * Inbound vault-access requests panel — shows requests from recruiters
 * asking for time-limited access to the candidate's vault. Candidate
 * can approve (issues a 24h access link + notifies recruiter) or decline.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function VaultAccessRequestsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/me/vault-access-requests`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      // Endpoint is candidate-only; recruiters/admins get 403 here and the panel
      // self-hides via the empty-state branch below. Log at debug level only.
      logger.debug('vault-access-requests skipped', e?.response?.status);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const respond = async (id, action) => {
    setBusyId(id);
    setError('');
    try {
      await axios.post(`${API}/me/vault-access-requests/${id}/${action}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Action failed.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return null;
  const pending = items.filter(i => i.status === 'pending');
  if (pending.length === 0 && items.length === 0) return null;

  return (
    <div className="rounded-2xl bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] p-5 mb-6" data-testid="vault-access-requests-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg text-[var(--vh-text-primary)]">Recruiter access requests</h3>
        <span className="text-xs text-[var(--vh-text-muted)]">{pending.length} pending</span>
      </div>
      {error && <p className="text-sm text-red-700 mb-2">{error}</p>}
      {pending.length === 0 ? (
        <p className="text-sm text-[var(--vh-text-muted)]">No pending requests. Past decisions are kept in your Audit Trail.</p>
      ) : (
        <ul className="space-y-3">
          {pending.map((r) => (
            <li key={r.id} className="p-4 rounded-xl bg-[var(--vh-bg-muted)] border border-[var(--vh-border)]" data-testid={`vault-access-request-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--vh-text-primary)]">
                    {r.recruiter_name || 'A recruiter'}
                    {r.recruiter_company ? ` · ${r.recruiter_company}` : ''}
                  </p>
                  <p className="text-xs text-[var(--vh-text-muted)] mt-0.5">{r.recruiter_email} · {fmt(r.created_at)}</p>
                  {r.message && <p className="text-sm text-[var(--vh-text-secondary)] mt-2">“{r.message}”</p>}
                  {!!(r.requested_sections || []).length && (
                    <p className="text-xs text-[var(--vh-text-muted)] mt-2">
                      Requesting: <span className="text-[var(--vh-text-secondary)]">{r.requested_sections.join(', ')}</span>
                    </p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => respond(r.id, 'approve')}
                    disabled={busyId === r.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white text-xs font-medium disabled:opacity-60"
                    data-testid={`vault-access-approve-${r.id}`}
                  >
                    {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Approve 24h
                  </button>
                  <button
                    type="button"
                    onClick={() => respond(r.id, 'decline')}
                    disabled={busyId === r.id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--vh-border)] text-[var(--vh-text-secondary)] hover:bg-[var(--vh-bg-base)] text-xs font-medium disabled:opacity-60"
                    data-testid={`vault-access-decline-${r.id}`}
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Decline
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
